/**
 * Ce qu'un visiteur a le droit de voir d'un profil.
 *
 * POURQUOI CE FICHIER : la règle vivait uniquement dans PeopleDetailContent,
 * et la carte l'ignorait — la bulle d'information affichait e-mail, téléphone
 * et adresse complète sans consulter un seul réglage, y compris pour les
 * profils dont la fiche masque justement l'adresse. Une règle de
 * confidentialité appliquée à un seul endroit n'est pas une règle.
 *
 * Les décisions sont ici, en fonctions pures, testables, et partagées par
 * tous les affichages d'un profil : la fiche, la carte, et ce qui viendra.
 */

/**
 * Types de profil dont l'adresse exacte ne s'affiche pas.
 *
 * 1 STUDENT, 3 INDIVIDUAL, 6 RETIRED : des personnes privées. Un artisan ou
 * un professionnel, lui, publie son adresse — c'est son établissement.
 */
const TYPES_ADRESSE_PRIVEE = [1, 3, 6];

/** Lit une valeur quelle que soit la casse rendue par ColdFusion. */
function lire(objet, cle) {
    if (!objet) return undefined;
    if (cle in objet) return objet[cle];
    const bas = cle.toLowerCase();
    if (bas in objet) return objet[bas];
    const haut = cle.toUpperCase();
    if (haut in objet) return objet[haut];
    return undefined;
}

/** Convertit un bit ColdFusion, une chaîne ou un booléen en booléen. */
export function versBooleen(valeur) {
    if (valeur === true || valeur === 1) return true;
    if (valeur === false || valeur === 0) return false;
    const s = String(valeur ?? '').trim().toLowerCase();
    if (s === '1' || s === 'true' || s === 'yes') return true;
    return false;
}

/**
 * Vrai si l'adresse exacte de ce profil doit rester cachée au visiteur.
 *
 * Deux motifs cumulés :
 *  - le TYPE de profil : une personne privée n'est pas un commerce
 *  - le STATUT de demandeur d'emploi, quel que soit le type — un employeur
 *    ne doit pas pouvoir déduire d'une adresse ce qu'il n'a pas à savoir
 *
 * DEUX EXCEPTIONS, toutes deux fondées sur la RELATION et non sur le profil :
 *
 *  - son propre profil : on voit toujours ses données ;
 *  - un VOISIN DU MEME QUARTIER : ils se connaissent déjà, l'adresse n'est
 *    pas une révélation. C'est ce qui donne sa valeur au quartier — y entrer
 *    ouvre ce que les voisins partagent entre eux. Un non-membre, lui, voit
 *    l'existence du quartier et ses membres, jamais une adresse précise.
 *
 * `relation` porte ce contexte : { estSonPropreProfil, partageUnQuartier }.
 * Les deux valent faux par défaut — le silence ne doit jamais révéler.
 */
export function adresseDoitEtreMasquee(profil, relation = {}) {
    if (!profil) return true;

    // Compatibilité : un booléen valait « est-ce mon profil ? ».
    const ctx = typeof relation === 'boolean' ? { estSonPropreProfil: relation } : (relation || {});

    if (ctx.estSonPropreProfil) return false;
    if (ctx.partageUnQuartier) return false;

    const type = Number(lire(profil, 'profileTypeId'));
    if (TYPES_ADRESSE_PRIVEE.includes(type)) return true;

    if (lire(profil, 'employmentStatus') === 'JOBSEEKER') return true;

    return false;
}

/**
 * Vrai si la POSITION exacte peut être affichée sur une carte.
 *
 * C'est la fuite que ce fichier corrige : la fiche masquait l'adresse en
 * toutes lettres tout en posant un marqueur sur la maison. Masquer un texte
 * et publier la coordonnée qu'il désigne ne protège rien.
 *
 * La règle est donc la même que pour l'adresse : ce qui vaut pour la rue
 * vaut pour le point sur la carte.
 */
export function positionPeutEtreAffichee(profil, relation = {}) {
    return !adresseDoitEtreMasquee(profil, relation);
}

/**
 * Les coordonnées reçues désignent-elles une MAISON ou un QUARTIER ?
 *
 * POURQUOI LA QUESTION SE POSE. L'API rend deux nombres, `latitude` et
 * `longitude`, et selon la personne qui regarde ce sont soit la porte exacte,
 * soit un point arrondi à ~100 m. Les deux ont la même forme. Une carte qui
 * ne saurait pas les distinguer poserait un point sur les deux — et
 * désignerait alors une maison au hasard dans le voisinage de la vraie.
 *
 * C'EST PIRE QU'UNE IMPRÉCISION, C'EST UNE ERREUR D'ACCUSATION : le point
 * tomberait sur la maison d'un tiers, qui n'a rien demandé et n'apparaît même
 * pas sur la carte. L'honnêteté visuelle n'est donc pas un raffinement
 * esthétique ; c'est la condition pour que l'approximation reste une
 * approximation au lieu de devenir une fausse précision.
 *
 * LE SERVEUR TRANCHE, PAS LE FRONT. `positionApprochee` est posé par
 * `peoples.cfc` là où la décision est prise. Le déduire ici du nombre de
 * décimales serait une heuristique fragile : une adresse exacte peut très
 * bien tomber sur un arrondi.
 *
 * DÉFAUT PRUDENT : sans drapeau — un serveur pas encore à jour, une réponse
 * d'un autre endpoint — on répond « approchée ». Dessiner un cercle là où un
 * point suffisait ne coûte qu'un peu de précision ; l'inverse désigne une
 * porte qu'on n'avait pas le droit de désigner.
 */
export function positionEstApprochee(profil) {
    if (!profil) return true;

    const brut = profil.positionApprochee
        ?? profil.positionapprochee
        ?? profil.POSITIONAPPROCHEE;

    if (brut === undefined || brut === null) return true;
    return versBooleen(brut);
}

/**
 * Vrai si ce champ de contact peut être affiché.
 *
 * DEUX CATÉGORIES, ET NE PAS LES CONFONDRE :
 *
 *   CONSTITUTIF   le nom et l'adresse. Adhérer à un quartier, c'est les
 *                 accepter : un quartier où l'on ignore qui habite où n'est
 *                 pas un quartier. Traité par `adresseDoitEtreMasquee`.
 *
 *   AU CHOIX      le téléphone, le GSM, l'e-mail. Être voisin n'oblige pas
 *                 à donner son numéro. C'est cette fonction.
 *
 * La version de v7.10 ouvrait TOUT à un voisin, ce qui était trop : le
 * partage d'un quartier n'emporte pas le consentement à être appelé.
 *
 * DEUX NIVEAUX DE RÉGLAGE, comme demandé :
 *
 *   Cabaza    `showPhone`, `showGsm`, `showEmail` — ce que voit n'importe qui
 *   quartier  `reglagesQuartier` — ce que voient ses voisins, EN PLUS
 *
 * Les réglages de quartier S'AJOUTENT et ne retranchent jamais : un numéro
 * déjà public ne se cache pas à ses voisins, ce serait absurde et créerait
 * deux vérités contradictoires. Quelqu'un peut en revanche masquer son
 * téléphone au monde et l'ouvrir à sa rue — c'est probablement le cas le
 * plus fréquent, et il était impossible jusqu'ici.
 *
 * Les défauts Cabaza sont ceux de la fiche : téléphone et GSM cachés,
 * e-mail visible. Un champ absent vaut donc « caché » pour les deux
 * premiers — le silence ne doit jamais publier un numéro.
 */
export function contactPeutEtreAffiche(profil, champ, relation = {}) {
    if (!profil) return false;

    const ctx = typeof relation === 'boolean' ? { estSonPropreProfil: relation } : (relation || {});
    if (ctx.estSonPropreProfil) return true;

    const reglages = {
        phone: { cle: 'showPhone', defaut: false },
        gsm:   { cle: 'showGsm',   defaut: false },
        email: { cle: 'showEmail', defaut: true }
    };

    const regle = reglages[champ];
    // Un champ sans réglage (société, site web) reste visible : c'est le
    // comportement actuel de la fiche, et le changer serait une autre
    // décision que la correction de cette fuite.
    if (!regle) return true;

    // Le réglage Cabaza d'abord : s'il ouvre, tout le monde voit, voisin
    // compris. Rien ne peut le restreindre ensuite.
    const valeur = lire(profil, regle.cle);
    let ouvertPartout = regle.defaut;
    if (valeur !== undefined && valeur !== null && valeur !== '') {
        ouvertPartout = versBooleen(valeur);
    }
    if (ouvertPartout) return true;

    // Sinon, le réglage de quartier peut ouvrir — mais SEULEMENT à un
    // voisin. Ouvrir son numéro à sa rue ne l'ouvre pas au monde entier.
    if (ctx.partageUnQuartier && ctx.reglagesQuartier) {
        return versBooleen(lire(ctx.reglagesQuartier, regle.cle));
    }

    return false;
}

/**
 * Ce qu'on affiche d'une adresse quand l'exacte est masquée : la localité.
 *
 * « 5660 Mariembourg » suffit à un voisin pour savoir qu'on est du coin, sans
 * livrer le numéro de porte.
 */
export function localiteApprochee(profil) {
    if (!profil) return '';
    const cp = String(lire(profil, 'postalCode') ?? '').trim();
    const ville = String(lire(profil, 'city') ?? '').trim();
    return [cp, ville].filter(Boolean).join(' ');
}
